import { useSendSMS } from '@hakimo-ui/hakimo/data-access';
import { LocationContact } from '@hakimo-ui/hakimo/types';
import { useState } from 'react';

interface SendArgs {
  message: string;
  fileList: File[];
  locationId?: number;
  locationAlarmId?: string;
  alarmGroupId?: string;
  isScan?: boolean;
}

/**
 * Which id ties the SMS to its alarm; exactly one goes on the wire.
 *
 * The alarm's updates are read back by location alarm id, so that is what
 * identifies it -- except on scan, where an alarm exists only as part of a
 * group. Scan is v2-only, so no version check is needed here.
 */
function alarmIdFields(args: SendArgs) {
  const { isScan, locationAlarmId, alarmGroupId } = args;

  return isScan
    ? { alarm_group_id: alarmGroupId }
    : { location_alarm_id: locationAlarmId };
}

export interface SMSFailure {
  contact: LocationContact;
  error: Error;
}

const toError = (reason: unknown): Error =>
  reason instanceof Error ? reason : new Error(String(reason));

export function useSMSFanout() {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<Set<string>>(new Set());
  const [failures, setFailures] = useState<SMSFailure[]>([]);
  const mutation = useSendSMS(() => undefined);

  const send = async (
    targets: LocationContact[],
    args: SendArgs
  ): Promise<boolean> => {
    if (targets.length === 0) return false;
    setSending(true);
    const { message, fileList, locationId } = args;
    const alarmFields = alarmIdFields(args);

    const results = await Promise.allSettled(
      targets.map((r) =>
        mutation.mutateAsync({
          to_number: r.phoneNumber,
          to_name: r.name,
          message,
          location_id: locationId,
          ...alarmFields,
          snapshots: fileList,
        })
      )
    );

    const nextSent = new Set(sent);
    const newFailures: SMSFailure[] = [];
    results.forEach((res, i) => {
      if (res.status === 'fulfilled') {
        nextSent.add(String(targets[i].id));
      } else {
        newFailures.push({ contact: targets[i], error: toError(res.reason) });
      }
    });

    setSent(nextSent);
    setFailures(newFailures);
    setSending(false);
    return newFailures.length === 0;
  };

  const reset = () => {
    setSent(new Set());
    setFailures([]);
  };

  return { sending, sent, failures, send, reset };
}
